import { useCallback, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import routesConfig from 'lib/routesConfig'
import appConfig from 'config/appConfig'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'
import appFeatures from 'config/appFeatures'
import { switchToOldUi } from 'redux/features/auth/authSlice'
import { AvailableSettings, SettingValue } from 'types/Settings'

export interface LogoutMenuLogicConfig {
  onLogout: () => void
  handleClose: () => void
}

export interface LogoutMenuLogic {
  isLicenseComplianceOn: boolean
  isLanguageMenuDialogOpen: boolean
  preferredLanguage: string
  handleLogout: () => void
  onCloseLanguageMenu: () => void
  onOpenLanguageMenu: () => void
  onOpenReviewLicense: () => void
  onSwitchToOldUi: () => void
}

export const useLogoutMenuLogic = ({ onLogout, handleClose }: LogoutMenuLogicConfig): LogoutMenuLogic => {
  const dispatch = useAppDispatch()

  const isLicenseComplianceOn =
    appFeatures.EnabledLicenseCompliance || isMyFeatureOn(FEATURES.EGD_React_License_Compliance_Rollout)

  const { preferredLanguage, serial, isCplAccount } = useAppSelector(_store => ({
    preferredLanguage: _store.app.language,
    serial: _store.auth.accessTokenObject?.serial,
    isCplAccount: !!_store.auth.accessTokenObject?.isCplAccount
  }))
  const [isLanguageMenuDialogOpen, setIsLanguageMenuDialogOpen] = useState(false)

  const handleLogout = useCallback(() => {
    handleClose()
    onLogout()
  }, [handleClose, onLogout])

  const onCloseLanguageMenu = useCallback(() => {
    setIsLanguageMenuDialogOpen(false)
    handleClose()
  }, [handleClose])

  const onOpenLanguageMenu = useCallback(() => setIsLanguageMenuDialogOpen(true), [])

  const onSwitchToOldUi = useCallback(
    () => dispatch(switchToOldUi({ [AvailableSettings.REACT_OPT_OUT]: SettingValue.ENABLED })),
    [dispatch]
  )

  const onOpenReviewLicense = useCallback(() => routesConfig.OVERVIEW_LICENSE_COMPLIANCE.goto(), [])

  return useMemo(
    () => ({
      isLicenseComplianceOn: appConfig.APP.IS_ADMIN && isLicenseComplianceOn && !!serial && !isCplAccount,
      isLanguageMenuDialogOpen,
      preferredLanguage,
      handleLogout,
      onCloseLanguageMenu,
      onOpenLanguageMenu,
      onOpenReviewLicense,
      onSwitchToOldUi
    }),
    [
      isLanguageMenuDialogOpen,
      preferredLanguage,
      onOpenReviewLicense,
      isLicenseComplianceOn,
      serial,
      isCplAccount,
      handleLogout,
      onCloseLanguageMenu,
      onOpenLanguageMenu,
      onSwitchToOldUi
    ]
  )
}
